import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { quoteApi, absoluteFileUrl } from "@/lib/phase3Entities";
import { apiError } from "@/lib/errors";
import { ActionMenu } from "@/components/ActionMenu";
import { LinkedContactPicker } from "@/components/LinkedContactPicker";

const STATUS_STYLES = {
  draft: "bg-chrome-mist text-carbon border-coach-silver",
  sent: "bg-coach-blue/10 text-coach-blue border-coach-blue",
  accepted: "bg-touring-lime/25 text-carbon border-touring-lime",
  declined: "bg-critical-red/10 text-critical-red border-critical-red",
  superseded: "bg-steel/10 text-steel border-steel",
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

export default function QuotesTab({ tourId, canWrite = true, refreshSignal = 0, onChanged, customerId = null }) {
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  const load = async () => {
    try { setQuotes(await quoteApi.list(tourId)); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tourId, refreshSignal]);
  const notify = () => { if (typeof onChanged === "function") onChanged(); };

  const create = async () => {
    setBusy(true);
    try {
      await quoteApi.create(tourId, { customer_facing_notes: notes || null });
      toast.success("New quote version created");
      setNotes("");
      await load();
      notify();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const patchBillingContact = async (quoteId, contact) => {
    try {
      await quoteApi.patch(quoteId, { billing_contact_id: contact?.id || null });
      toast.success(contact ? "Billing Contact linked" : "Billing Contact unlinked");
      await load();
      notify();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, s) => {
    let reason = null;
    if (s === "declined") {
      reason = window.prompt("Reason for decline?") || "";
      if (!reason) return;
    }
    try { await quoteApi.setStatus(id, s, reason); await load(); notify(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="quotes-tab">
      {canWrite && (
        <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="quotes-create-panel">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-widest text-steel">
              {quotes.length === 0 ? "Create the first Quote" : "Create a new Quote version"}
            </div>
            <span className="text-[10px] uppercase tracking-widest text-carbon border border-coach-silver rounded-sm px-1.5 py-0.5" data-testid="quotes-tab-version-badge">
              Version {(quotes[0]?.version || 0) + 1}
            </span>
          </div>
          <label className="block mb-2">
            <div className="text-[11px] text-carbon mb-1">Customer-facing notes for this Quote version</div>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="These notes will appear on the customer Quote/PDF."
              className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
              data-testid="quote-notes" />
          </label>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-steel">
              Deep-copies current customer, tour, assets, drivers, and pricing into a frozen snapshot,
              then generates a branded PDF (SHA-256 recorded).
            </div>
            <button onClick={create} disabled={busy}
              className="bg-coach-blue text-white text-[12.5px] font-medium px-3 py-2 rounded-sm disabled:opacity-40"
              data-testid="quote-create">
              {busy ? "Generating…" : (quotes.length === 0 ? "Create Quote" : "Create New Version")}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-coach-silver rounded-md" data-testid="quotes-list">
        {quotes.length === 0 && (
          <div className="p-6 text-center text-steel text-[13px]">No quote versions yet.</div>
        )}
        {quotes.map((q) => (
          <div key={q.id} data-testid={`quote-${q.id}`}
            className="border-b border-coach-silver/60 last:border-0 p-4 flex items-center gap-4">
            <div className="w-16 text-center">
              <div className="text-[10.5px] uppercase tracking-widest text-steel">Version</div>
              <div className="font-display text-2xl text-deep-road-navy">v{q.version}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-carbon">
                <span className={`inline-block px-2 py-0.5 border rounded-sm text-[10.5px] uppercase tracking-widest mr-2 ${STATUS_STYLES[q.status] || ""}`}
                  data-testid={`quote-status-${q.id}`}>
                  {q.status}
                </span>
                <span className="font-medium">{fmtMoney(q?.snapshot_pricing?.totals?.customer_total)}</span>
                <span className="text-steel"> · {q?.snapshot_tour?.tour_number}</span>
              </div>
              <div className="text-[11px] text-steel mt-1 truncate">
                SHA-256 <span className="font-mono">{q.pdf_sha256?.slice(0, 16)}…</span>
                &nbsp;·&nbsp; {q.created_at?.replace("T", " ").slice(0, 16)} UTC
              </div>
              {q.customer_facing_notes && (
                <div className="text-[12px] text-carbon mt-1 italic">"{q.customer_facing_notes}"</div>
              )}
              {q.status === "draft" && canWrite && (
                <div className="mt-2" data-testid={`quote-billing-contact-picker-${q.id}`}>
                  <LinkedContactPicker
                    label="Billing Contact (optional — freezes on Send/Accept/Decline)"
                    testidPrefix={`quote-billing-contact-${q.id}`}
                    customerId={customerId}
                    value={q.billing_contact || (q.billing_contact_id ? { id: q.billing_contact_id } : null)}
                    onChange={(c) => patchBillingContact(q.id, c)}
                  />
                </div>
              )}
              {q.status !== "draft" && q.snapshot_billing_contact && (
                <div className="mt-2 text-[11.5px] text-steel" data-testid={`quote-frozen-billing-${q.id}`}>
                  Frozen Billing Contact: {q.snapshot_billing_contact.display_name}
                  {q.snapshot_billing_contact.primary_email && ` · ${q.snapshot_billing_contact.primary_email}`}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {q.pdf_url && (
                <a href={absoluteFileUrl(q.pdf_url)} target="_blank" rel="noreferrer"
                  className="text-coach-blue hover:underline text-[12.5px]"
                  data-testid={`quote-pdf-${q.id}`}>View PDF</a>
              )}
              {canWrite && q.status === "draft" && (
                <button onClick={() => setStatus(q.id, "sent")}
                  className="bg-coach-blue text-white text-[12px] px-2 py-1 rounded-sm"
                  data-testid={`quote-send-${q.id}`}>Mark Sent</button>
              )}
              {canWrite && (q.status === "sent" || q.status === "draft") && (
                <>
                  <button onClick={() => setStatus(q.id, "accepted")}
                    className="border border-touring-lime bg-touring-lime/20 text-carbon text-[12px] px-2 py-1 rounded-sm"
                    data-testid={`quote-accept-${q.id}`}>Accept</button>
                  <button onClick={() => setStatus(q.id, "declined")}
                    className="border border-critical-red text-critical-red text-[12px] px-2 py-1 rounded-sm"
                    data-testid={`quote-decline-${q.id}`}>Decline</button>
                </>
              )}
              {canWrite && (
                <ActionMenu
                  testid={`quote-actions-${q.id}`}
                  onView={q.pdf_url ? () => window.open(absoluteFileUrl(q.pdf_url), "_blank") : undefined}
                  onArchive={
                    // Only DRAFT quotes are discardable. Any other status is
                    // a frozen snapshot and shows View + Supersede only.
                    q.status === "draft"
                      ? async () => {
                          if (!window.confirm(`Discard draft Quote v${q.version}? This cannot be undone.`)) return;
                          try {
                            await quoteApi.discardDraft(q.id);
                            toast.success(`Draft v${q.version} discarded`);
                            await load();
                            notify();
                          } catch (e) { toast.error(apiError(e)); }
                        }
                      : ["sent", "accepted", "declined", "superseded", "expired"].includes(q.status)
                        ? async () => {
                            if (!window.confirm(`Supersede this quote and create a new v${q.version + 1} draft?`)) return;
                            try {
                              await quoteApi.setStatus(q.id, "superseded", "user_superseded");
                              await quoteApi.create(tourId, {});
                              toast.success("New draft created");
                              await load();
                              notify();
                            } catch (e) { toast.error(apiError(e)); }
                          }
                        : undefined
                  }
                  archiveLabel={q.status === "draft" ? "Discard Draft" : "Supersede →"}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
